import { useCallback } from "react"

import {
  useTransportMutation,
  useTransportQuery,
  type TransportRequestError,
} from "@/transport"
import type { MutateOptions } from "@tanstack/react-query"

import type {
  TransportOperationRequest,
  TransportOperationResponse,
} from "@/types_api/backend/transport/operations"

interface AudioSignedUrlInput {
  bucket: string
  audioKey: string
  asTestCase?: boolean
}

interface PublicAudioSignedUrlInput extends AudioSignedUrlInput {
  password?: string | null
  reportId?: string | null
  runId?: string | null
  simulationOutputId?: string | null
}

interface AudioUploadUrlInput {
  fileType: string
  fileExtension: string
}

interface AudioUploadUrlResult {
  url: string
  fields: Record<string, string>
  s3Bucket: string
  s3Key: string
  maxSizeBytes?: number
}

interface AudioSignedUrlQueryOptions {
  enabled?: boolean
  gcTime?: number
  refetchInterval?: false | number
  refetchOnMount?: boolean
  refetchOnWindowFocus?: boolean
  staleTime?: number
}

type AudioUploadUrlResponse = TransportOperationResponse<"getAudioUploadUrl">
type AudioUploadUrlMutationRequest =
  TransportOperationRequest<"getAudioUploadUrl">
type AudioUploadUrlMutationOptions = Omit<
  MutateOptions<
    AudioUploadUrlResponse,
    TransportRequestError,
    AudioUploadUrlMutationRequest
  >,
  "onSettled" | "onSuccess"
> & {
  onSettled?: (
    data: AudioUploadUrlResult | undefined,
    error: TransportRequestError | null,
    variables: AudioUploadUrlMutationRequest,
    context: unknown,
  ) => void
  onSuccess?: (
    data: AudioUploadUrlResult,
    variables: AudioUploadUrlMutationRequest,
    context: unknown,
  ) => void
}
interface AudioUrlResponse {
  url: string
}

export function toAudioSignedUrlRequest(input: AudioSignedUrlInput) {
  return {
    bucket: input.bucket,
    audio_key: input.audioKey,
    as_test_case: input.asTestCase,
  }
}

export function toPublicAudioSignedUrlRequest(
  input: PublicAudioSignedUrlInput,
): TransportOperationRequest<"getPublicAudioSignedUrl"> {
  return {
    ...toAudioSignedUrlRequest(input),
    password: input.password ?? undefined,
    report_id: input.reportId ?? undefined,
    run_id: input.runId ?? undefined,
    simulation_output_id: input.simulationOutputId ?? undefined,
  }
}

export function toAudioUploadUrlRequest(
  input: AudioUploadUrlInput,
): TransportOperationRequest<"getAudioUploadUrl"> {
  return {
    file_type: input.fileType,
    file_extension: input.fileExtension,
  }
}

export function mapAudioSignedUrlResponse(response: AudioUrlResponse) {
  return response.url
}

export function mapAudioUploadUrlResponse(
  response: AudioUploadUrlResponse,
): AudioUploadUrlResult {
  return {
    url: response.url,
    fields: response.fields,
    s3Bucket: response.s3_bucket,
    s3Key: response.s3_key,
    maxSizeBytes: response.max_size_bytes,
  }
}

/**
 * Raised when a file exceeds the size the API reported for its upload URL.
 *
 * Carries a message meant for the person who picked the file. Callers that render errors through
 * `getUserFacingErrorMessage` need this type to tell it apart from a transport failure, since that
 * formatter deliberately never surfaces a bare `Error.message`.
 */
export class AudioFileTooLargeError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "AudioFileTooLargeError"
  }
}

export async function uploadAudioFileWithSignedPost({
  url,
  fields,
  file,
  maxSizeBytes,
  signal,
}: {
  url: string
  fields: Record<string, string>
  file: File
  maxSizeBytes?: number
  signal?: AbortSignal
}) {
  // The signed post carries the same cap, but exceeding it there comes back as an opaque S3
  // rejection. Checking first means the file never goes over the wire and the reason is sayable.
  if (maxSizeBytes && file.size > maxSizeBytes) {
    const maxMegabytes = Math.floor(maxSizeBytes / (1024 * 1024))
    throw new AudioFileTooLargeError(
      `Audio file is too large. The maximum size is ${maxMegabytes} MB.`,
    )
  }

  const formData = new FormData()
  for (const [key, value] of Object.entries(fields)) {
    formData.append(key, value)
  }
  formData.append("file", file)

  const response = await fetch(url, {
    method: "POST",
    body: formData,
    signal,
  })

  if (!response.ok) {
    let responseBody = ""
    try {
      responseBody = await response.clone().text()
    } catch {
      // Best-effort diagnostic only; preserve the original upload failure.
    }

    const details = [response.status, response.statusText, responseBody]
      .filter(Boolean)
      .join(" ")
    throw new Error(`Audio upload failed with status ${details}`)
  }

  return response
}

export function useAudioSignedUrlTransport(
  input: AudioSignedUrlInput,
  options?: AudioSignedUrlQueryOptions,
) {
  return useTransportQuery<"getAudioSignedUrl", string>({
    operation: "getAudioSignedUrl",
    request: toAudioSignedUrlRequest(input),
    options: {
      ...options,
      select: mapAudioSignedUrlResponse,
    },
  })
}

export function usePublicAudioSignedUrlTransport(
  input: PublicAudioSignedUrlInput,
  options?: AudioSignedUrlQueryOptions,
) {
  return useTransportQuery<"getPublicAudioSignedUrl", string>({
    operation: "getPublicAudioSignedUrl",
    request: toPublicAudioSignedUrlRequest(input),
    options: {
      ...options,
      select: mapAudioSignedUrlResponse,
    },
  })
}

export function useAudioPeaksUrlTransport(
  input: AudioSignedUrlInput,
  options?: AudioSignedUrlQueryOptions,
) {
  return useTransportQuery<"getAudioPeaksUrl", string>({
    operation: "getAudioPeaksUrl",
    request: toAudioSignedUrlRequest(input),
    options: {
      ...options,
      select: mapAudioSignedUrlResponse,
    },
  })
}

export function useAudioUploadUrlTransport() {
  const mutation = useTransportMutation("getAudioUploadUrl")

  const mutate = useCallback(
    (input: AudioUploadUrlInput, options?: AudioUploadUrlMutationOptions) => {
      if (!options) {
        return mutation.mutate(toAudioUploadUrlRequest(input), undefined)
      }

      const { onSettled, onSuccess, ...restOptions } = options
      return mutation.mutate(toAudioUploadUrlRequest(input), {
        ...restOptions,
        onSettled: (raw, error, variables, context) => {
          onSettled?.(
            raw ? mapAudioUploadUrlResponse(raw) : undefined,
            error,
            variables,
            context,
          )
        },
        onSuccess: (raw, variables, context) => {
          onSuccess?.(mapAudioUploadUrlResponse(raw), variables, context)
        },
      })
    },
    [mutation],
  )
  const mutateAsync = useCallback(
    async (input: AudioUploadUrlInput) =>
      mapAudioUploadUrlResponse(
        await mutation.mutateAsync(toAudioUploadUrlRequest(input)),
      ),
    [mutation],
  )
  const data = mutation.data
    ? mapAudioUploadUrlResponse(mutation.data)
    : undefined

  return {
    ...mutation,
    data,
    mutate,
    mutateAsync,
  }
}
